// Push Notification utilities for Choluteca.net

const API = process.env.REACT_APP_BACKEND_URL || '';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Get current notification permission status
export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'granted', 'denied', or 'default'
}

// Register service worker for push
async function registerServiceWorker() {
  try {
    const registration = await navigator.serviceWorker.register('/sw-push.js');
    console.log('[Push] Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('[Push] Service Worker registration failed:', error);
    throw error;
  }
}

// Subscribe to push notifications
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Push notifications no son soportadas en este navegador');
  }

  // Request permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Permiso de notificaciones denegado');
  }

  // Register service worker
  const registration = await registerServiceWorker();
  
  // Wait for service worker to be ready
  await navigator.serviceWorker.ready;

  // Get VAPID key
  let vapidKey = VAPID_PUBLIC_KEY;
  if (!vapidKey) {
    try {
      const response = await fetch(`${API}/api/push/vapid-key`);
      const data = await response.json();
      vapidKey = data.publicKey;
    } catch (error) {
      console.error('[Push] Failed to get VAPID key:', error);
      throw new Error('No se pudo obtener la clave de notificaciones');
    }
  }

  // Subscribe to push
  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey)
    });

    console.log('[Push] Subscription:', subscription);

    // Send subscription to server
    const response = await fetch(`${API}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        }
      })
    });

    if (!response.ok) {
      throw new Error('Error al registrar la suscripción');
    }

    const data = await response.json();
    console.log('[Push] Subscribed successfully:', data);
    
    return { success: true, message: data.message };
  } catch (error) {
    console.error('[Push] Subscription failed:', error);
    throw error;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Notify server
      await fetch(`${API}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {}
        })
      });

      // Unsubscribe locally
      await subscription.unsubscribe();
      console.log('[Push] Unsubscribed successfully');
    }
    
    return { success: true };
  } catch (error) {
    console.error('[Push] Unsubscribe failed:', error);
    throw error;
  }
}

// Check if already subscribed
export async function isSubscribedToPush() {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    return false;
  }
}
